import type { ARIARole } from "aria-query";

import { isNil } from "@eroad/helpers/isNil";
import type { MaybeNull } from "@eroad/types";

import { Driver } from "../../../drivers/Driver";

/**
 * Tooltip driver is used to test and interact with the Tooltip component in unit tests.
 * You instantiate a TooltipDriver by passing in a `DriverOptions` object related to the base component of the tooltip.
 *
 * @example
 * ```ts
 * const tooltip = new TooltipDriver({ text: "Hello world" }); // Used to query the base tooltip component. In this case, that is a text.
 * const tooltip = new TooltipDriver({ role: ButtonDriver.ROLE, { name: "Hello world"}}); // Used to query the base tooltip component. In this case, that is a button with the name "Hello world".
 * ```
 */
class TooltipDriver extends Driver<HTMLDivElement> {
  static readonly ROLE: ARIARole = "tooltip";

  /**
   * Gets the tooltip element.
   *
   * @example
   * ```tsx
   * const Component = () => (
   *   <Tooltip text="Yes! I'm here.">
   *     <span>Hello world</span>
   *   </Tooltip>
   * );
   *
   * it("should render tooltip on hover", async () => {
   *   const tooltipDriver = new TooltipDriver({ text: "Hello world" });
   *   await tooltipDriver.hoverOverTooltip();
   *
   *   const tooltip = await tooltipDriver.getTooltip()
   *   expect(tooltip).toHaveTextContent("Yes! I'm here.");
   * });
   * ```
   */
  async getTooltip(): Promise<HTMLDivElement | null> {
    try {
      return await this.screen.findByRole<HTMLDivElement>(TooltipDriver.ROLE);
    } catch {
      return null;
    }
  }

  /**
   * Hovers over the tooltip to render it.
   *
   * @example
   * ```tsx
   * await tooltipDriver.hoverOverTooltip();
   * ```
   */
  async hoverOverTooltip(): Promise<void> {
    await this.user.hover(this.element);
  }

  /**
   * Unhovers over the tooltip to hide it.
   *
   * @example
   * ```tsx
   * await tooltipDriver.unhoverOverTooltip();
   * ```
   */
  async unhoverOverTooltip(): Promise<void> {
    await this.user.unhover(this.element);
  }

  /**
   * Gets the arrow element of the tooltip.
   *
   * @example
   * ```tsx
   * expect(await tooltipDriver.getArrowElement()).toBeInTheDocument();
   * expect(await tooltipDriver.getArrowElement()).not.toBeInTheDocument();
   * ```
   */
  async getArrowElement(): Promise<MaybeNull<ChildNode>> {
    const tooltip = await this.getTooltip();

    if (isNil(tooltip?.parentElement?.previousSibling)) {
      return null;
    }

    return tooltip.parentElement.previousSibling;
  }
}

export { TooltipDriver };
